import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import Swal from 'sweetalert2'
import {PageLink, PageTitle} from '../../_metronic/layout/core'
import {PageOverlayer} from '../../components/PageOverlayer'
import {IntervalsRoomRequest} from '../../coreGlobal/models/room'
import {RoomFormProvider, useRoomForm} from './core/RoomFormProvider'
import {RoomForm} from './components/RoomForm'
import {roomSchema} from './core/validation/roomVali'
import {BuildRequest} from './core/BuildRequest'
import {getRoomById, updateRoom} from './core/_request'
import {showRequestError} from './core/showRequestError'
import {TAdminRoomForm, TAdminRoomRequest} from './core/_models'

const roomBreadcrumbs: Array<PageLink> = [
  {
    title: 'Admin',
    path: '/admin/rooms',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const groupByDay = (intervals: any[] = []): IntervalsRoomRequest[][] => {
  return intervals.reduce(
    (week: IntervalsRoomRequest[][], interval) => {
      week[interval.dayOfWeek].push({
        day: interval.dayOfWeek,
        start: interval.startTime,
        end: interval.endTime,
      })
      return week
    },
    [[], [], [], [], [], [], []]
  )
}

const Update = () => {
  const {id} = useParams()
  const {navigate, setLoading, setIntervals, getDataFranchises, getDataCategories, getDataProducts, getDataMachines} =
    useRoomForm()
  const [loadingPage, setLoadingPage] = useState(true)

  const formik = useFormik<TAdminRoomForm>({
    initialValues: {
      franchise: null,
      name: '',
      syncName: '',
      color: '#000000',
      categories: [],
      products: [],
      machines: [],
      intervals: [],
      maxScheduleDate: 0,
    },
    validationSchema: roomSchema,
    onSubmit: (values) => submit(BuildRequest(values)),
  })

  useEffect(() => {
    const init = async () => {
      try {
        const room = await getRoomById(String(id))
        const franchiseId = room.franchise?.id ?? null
        const categories = room.categories.map((category) => category.id)
        const intervals = groupByDay(room.intervals)

        await Promise.all([
          getDataFranchises(),
          getDataCategories(),
          getDataProducts(categories),
          getDataMachines(franchiseId),
        ])

        formik.setValues({
          franchise: franchiseId,
          name: room.name,
          syncName: room.syncName ?? '',
          color: room.color,
          categories,
          products: room.products.map((product) => product.id),
          machines: room.machines.map((machine) => machine.id),
          intervals,
          maxScheduleDate: room.maxScheduleDate,
        })
        // Cópia para o repeater não editar os mesmos objetos do formik; dia sem horário ganha um campo vazio
        setIntervals(
          intervals.map((dayIntervals, day) =>
            dayIntervals.length
              ? dayIntervals.map((interval) => ({...interval}))
              : [{day, start: '', end: ''}]
          )
        )
      } catch (error: any) {
        await showRequestError(error)
      } finally {
        setLoadingPage(false)
      }
    }

    init()
  }, [])

  const submit = async (req: TAdminRoomRequest) => {
    const result = await Swal.fire({
      title: 'Deseja atualizar a sala?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary',
      },
    })
    if (!result.isConfirmed) return

    setLoading(true)
    try {
      await updateRoom(String(id), req)
      await Swal.fire({
        position: 'center',
        icon: 'success',
        title: 'Salvo com sucesso',
        showConfirmButton: false,
        timer: 1500,
      })
      navigate('/admin/rooms', {replace: true})
    } catch (error: any) {
      await showRequestError(error)
    } finally {
      setLoading(false)
    }
  }

  return loadingPage ? <PageOverlayer /> : <RoomForm formik={formik} isEdit />
}

export const RoomEditPage = () => {
  return (
    <>
      <PageTitle breadcrumbs={roomBreadcrumbs}>Atualizar Sala</PageTitle>
      <RoomFormProvider>
        <Update />
      </RoomFormProvider>
    </>
  )
}
